import React from 'react'
import { graphql } from 'gatsby'

import ReportFeaturedTitle from '@/components/models/report/featured-title/featured-title'
import ReportFooterDownload from '@/components/models/report/footer-download/footer-download'
import ReportHero from '@/components/models/report/hero/hero'
import ReportHighlightsSection from '@/components/models/report/highlights-section/highlights-section'
import ReportModalForm from '@/components/models/report/modal-form/modal-form'
import ReportSlidesSection from '@/components/models/report/slides-section/slides-sections'
import ReportSubscribeForm from '@/components/models/report/subscribe-form/subscribe-form'
import ReportTableContent from '@/components/models/report/table-content/table-content'
import ReportVideoBanner from '@/components/models/report/video-banner/video-banner'
import ReportInterviews from '@/components/models/report/interviews-section/interviews-section'
import ReportTalks from '@/components/models/report/talks-section/talks-section'
import ReportPersons from '@/components/persons/persons'

import LayoutReport from '@/layouts/layoutReport'

import Seo from '@/components/seo/seo'
import { Microdata } from '@/components/seo/microdata'

import '../../components/models/report/report-base.scss'

// @TODO: Migrate to TypeScript

export const Head = ({ data: { datoCmsPctReport }, location }) => {
  return (
    <>
      <Seo data={datoCmsPctReport} location={location} />
      <Microdata data={datoCmsPctReport} location={location} />
    </>
  )
}

const Report = ({ data: { datoCmsPctReport }, location }) => {
  const slides =
    datoCmsPctReport.slides?.length > 0
      ? datoCmsPctReport.slides.map((slide, index) => (
          <ReportSlidesSection
            {...slide}
            index={index}
            key={`slide-section-${slide?.anchor ?? index}`}
          />
        ))
      : null

  return (
    <LayoutReport location={location} shareButtonsScrollTop="450">
      <ReportHero
        {...datoCmsPctReport?.hero?.[0]}
        cta={datoCmsPctReport?.download?.[0].cta[0]}
      />
      <ReportVideoBanner {...datoCmsPctReport?.videoBanner?.[0]} />
      <ReportTableContent {...(datoCmsPctReport?.tableOfContents?.[0] ?? {})} />
      <ReportFeaturedTitle {...datoCmsPctReport?.featuredTitle?.[0]} />
      {slides}
      <ReportTalks {...(datoCmsPctReport?.talks?.[0] ?? {})} />
      <ReportInterviews {...datoCmsPctReport?.interviews?.[0]} />
      <ReportHighlightsSection {...(datoCmsPctReport?.highlights?.[0] ?? {})} />
      <ReportPersons
        title={datoCmsPctReport?.leaders?.[0]?.title}
        persons={datoCmsPctReport?.leaders?.[0]?.leaders.map(
          (leader) => leader?.person
        )}
      />
      <ReportSubscribeForm {...datoCmsPctReport?.subscribeForm?.[0]} />
      <ReportFooterDownload {...datoCmsPctReport?.download?.[0]} />
      <ReportModalForm {...datoCmsPctReport?.form?.[0]} location={location} />
    </LayoutReport>
  )
}

export default Report

export const ReportQuery = graphql`
  fragment DatoCmsBannerWithCta on DatoCmsBannerWithCta {
    __typename
    title
    text
    cta {
      ...Cta
    }
    background {
      url
      title
      width
      height
    }
  }
  fragment DatoCmsHero on DatoCmsHero {
    __typename
    title
    subtitle
    text
  }
  fragment DatoCmsTableContent on DatoCmsTableContent {
    __typename
    number
    title
    content {
      value
    }
    subtitle
    tag
  }
  query Report($slug: String) {
    datoCmsPctReport(slug: { eq: $slug }) {
      __typename
      originalId
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      seo {
        title
        description
        image {
          url
        }
      }
      meta {
        firstPublishedAt
        publishedAtTimestamp: publishedAt(formatString: "X")
      }
      title
      slug
      hero {
        ... on DatoCmsHero {
          ...DatoCmsHero
        }
      }
      videoBanner {
        ... on DatoCmsVideoBanner {
          __typename
          text
          background {
            isImage
            url
            title
            width
            height
            gatsbyImageData(width: 1400)
            video {
              thumbnailUrl
            }
          }
        }
      }
      tableOfContents {
        ... on DatoCmsTable {
          __typename
          title
          contents {
            ... on DatoCmsTableContent {
              ...DatoCmsTableContent
            }
          }
        }
      }
      talks {
        ... on DatoCmsTalksSection {
          __typename
          title
          talks {
            title
            image {
              gatsbyImageData(height: 48)
              alt
            }
            text
          }
        }
      }
      highlights {
        ... on DatoCmsHighlightsSection {
          __typename
          title
          highlights {
            ... on DatoCmsHighlight {
              preTitle
              title
              image {
                gatsbyImageData(width: 1024, height: 1024)
                alt
              }
              content {
                value
              }
            }
          }
          bannerImage {
            gatsbyImageData(
              width: 2560
              imgixParams: { fit: "crop", crop: "focalpoint" }
            )
            alt
          }
          bannerTitle
          bannerCta {
            ...Cta
          }
        }
      }
      featuredTitle {
        ...DatoCmsHero
      }
      interviews {
        ...InterviewsSection
      }
      subscribeForm {
        ... on DatoCmsBannerWithCta {
          ...DatoCmsBannerWithCta
        }
      }
      download {
        ... on DatoCmsBannerWithCta {
          ...DatoCmsBannerWithCta
        }
      }
      leaders {
        id
        title
        leaders: leader {
          __typename
          id
          person {
            __typename
            id
            firstName
            secondName
            job
            linkedin
            company {
              externalName
              url
            }
            photo {
              alt
              gatsbyImageData(
                width: 384
                height: 372
                imgixParams: {
                  w: "384"
                  h: "372"
                  fit: "crop"
                  crop: "focalpoint"
                }
              )
            }
          }
          bio
        }
      }
      form {
        ... on DatoCmsForm {
          ...FormBlock
        }
      }
      slides {
        ... on DatoCmsSlidesSection {
          ...DatoCmsSlidesSection
        }
      }
    }
  }
`
